import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { LoadingSpinner, Table, ColumnSort } from 'legacy-packages/ft-cb';
import findResourceInCourseOutline from 'legacy-src/utility/helpers/findResourceInCourseOutline';
import { getTopicQuestionsFilter } from 'legacy-packages/unit-outline/src/components/_shared/util';
import { getSignedUnitResource } from 'legacy-src/utility/utils';
import ResourceTypes, {
  ResourceContentTypes,
  SubunitAssessmentTypes,
} from 'legacy-packages/unit-outline/src/constants';
import { mapAssignmentsToResourceIds } from 'chameleon/ui-stack/ui/units/utils';
import Description from './Columns/Description';
import ActionContainer from './Columns/Action/Container';
import TagWithDescriptionPopover from 'legacy-src/utility/TagWithDescriptionPopover';
import PracticeSkillsPopOver from 'chameleon-packages/units/components/PracticeSkills';
import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import {
  handleTelemetry,
  TELEMETRY_EVENTS,
} from 'legacy-src/utility/telemetry';
import { getAuthenticatedProfessionalLearningLink } from 'chameleon/ui-stack/ui/units/subunit-content/ProfessionalLink';

const getUpdatedTagsStructure = (tags, type) =>
  tags.map((tag) => {
    const shortTag = tag.split(':');
    return {
      title: shortTag[0],
      label: shortTag[0],
      description: tag,
      accessibilityTitle: `${type} ${shortTag[0]}`,
    };
  });

const getTags = (displayTags, tagsType) => {
  let { [tagsType]: tags = [] } = JSON.parse(displayTags || '{}');
  return getUpdatedTagsStructure(tags, tagsType);
};

const ResultsTable = (props) => {
  const {
    isLoading,
    resources,
    pushParamsToHistory,
    units,
    isPreApSubject,
    subjectId,
    widgetTagColumn,
    widgetTagColumnLabel,
    unitsLabel,
    router,
    searchTerm,
    searchType,
    sortBy,
    teacherAssignments,
    loadingAssignments,
    searchResultsTags,
    subunitLabel,
  } = props;
  const [ppcsCourseOutlineData, setPPCsCourseOutlineData] = useState([]);

  const removePEAssessment = ({ resource }) => {
    const { id } = resource;
    const resourceInfo = findResourceInCourseOutline(id, units);
    if (!resourceInfo) {
      return true;
    }
    return resourceInfo.subunitType !== SubunitAssessmentTypes.PE;
  };

  useEffect(() => {
    if (!loadingAssignments) {
      const ppcResources = resources.filter(
        (r) => r.resource.__typename === ResourceTypes.ASSESSMENT,
      );
      let ppcCOResources = ppcResources
        .map(({ resource: { id } }) => findResourceInCourseOutline(id, units))
        .filter((r) => !!r);

      // FY-23754 filter out all PE in search result
      ppcCOResources = ppcCOResources.filter((r) => {
        return r.subunitType !== SubunitAssessmentTypes.PE;
      });

      let ppcAssignments = [];
      if (Object.keys(teacherAssignments || {}).length > 0) {
        ppcAssignments = mapAssignmentsToResourceIds(
          teacherAssignments,
          ppcCOResources.map((r) => r.resource),
        );
      }

      setPPCsCourseOutlineData(
        ppcCOResources.map((r) => ({
          ...r,
          assignments:
            ppcAssignments.filter(
              (assignment) =>
                assignment.assessment.id === r.resource.assessmentId,
            ) || [],
        })),
      );
    }
  }, [resources, teacherAssignments, loadingAssignments]);

  const onChangeSortBy = (sortQueryParam) => {
    pushParamsToHistory({ sort: sortQueryParam });
  };

  const onNameClick = async (row) => {
    const {
      resource: { id, __typename: resourceType, url, contentType },
    } = row;

    if (resourceType === ResourceTypes.EMBEDDED_VIDEO) {
      pushParamsToHistory({
        apd: url,
      });
    } else if (resourceType === ResourceTypes.PRACTICE_QUESTIONS) {
      const coResource = findResourceInCourseOutline(id, units);
      const { subunitMetadata: metadata, unit } = coResource;
      const { number: unitNumber } = unit;

      const topicQuestionsFilter = isPreApSubject
        ? metadata
        : getTopicQuestionsFilter({
            subjectId: parseInt(subjectId, 10),
            unitNumber,
            metadata,
            widgetTagColumn,
            skipCustomFilters: false,
            unitTitle: unit.title,
            unitsLabel,
            subunitLabel,
          });

      router.push({
        pathname: `/${subjectId}/question_bank/questions/filters`,
        query: {
          searchTerm,
          searchType: 'all',
          unit: unitNumber,
        },
        state: {
          topicQuestionsFilter,
          questionBankPath: '/questions',
        },
      });
    } else if (resourceType === ResourceTypes.URL) {
      let signedURL;
      if (contentType === ResourceContentTypes.EXTERNAL_LINK) {
        signedURL = url;
      } else if (contentType === ResourceContentTypes.PROFESSIONAL_LINK) {
        signedURL = getAuthenticatedProfessionalLearningLink(subjectId, url);
      } else {
        signedURL = await getSignedUnitResource(url);
      }
      window.open(signedURL, '_blank').focus();
    } else if (resourceType === ResourceTypes.SOURCE) {
      const signedURL =
        contentType === ResourceContentTypes.EXTERNAL_LINK
          ? url
          : await getSignedUnitResource(url);
      handleTelemetry('resource_search', TELEMETRY_EVENTS.openSourceResource, {
        resourceId: id,
        contentType,
        subjectId,
      });
      window.open(signedURL, '_blank').focus();
    } else if (resourceType === ResourceTypes.ASSESSMENT) {
      const coResource = findResourceInCourseOutline(id, units);
      const {
        resource: { assessmentId: quizId },
      } = coResource;

      let returnPath = `/${subjectId}/home?mode=search`;
      if (searchTerm.trim()) {
        returnPath += `&searchTerm=${searchTerm}`;
      }

      if (searchType.trim()) {
        returnPath += `&searchType=${searchType}`;
      }

      if (sortBy.trim()) {
        returnPath += `&sort=${sortBy}`;
      }

      router.push({
        pathname: `/${subjectId}/assignments`,
        query: {
          quizId,
          returnPath,
        },
      });
    } else if (resourceType === ResourceTypes.SPP) {
      const { location } = useNavigation({
        router,
      });

      const itemId = row.resource.assessmentId;

      if (!itemId) {
        return null;
      }

      router.push({
        pathname: `/${subjectId}/assessments/spp/preview/${itemId}`,
        state: {
          returnPath: `${location.pathname}${location.search}`,
        },
      });
    }
  };

  const columns = [
    {
      id: 'title',
      headerClassName: 'w-5/12 bg-gray-200 rounded-l-0.4',
      className: 'w-5/12',
      label: (
        <ColumnSort
          className="cursor-pointer"
          selection={sortBy}
          onChange={onChangeSortBy}
          columnId="title"
          isDisabled={false}
          isReversed
        >
          Resource Title
        </ColumnSort>
      ),
      body: (row) => {
        const { resource, resourceCount: count } = row;

        return (
          <Description
            resource={resource}
            count={count}
            onNameClick={() => onNameClick(row)}
          />
        );
      },
    },
    ...(searchResultsTags ? searchResultsTags.split(',') : [])
      .filter((tag) => tag !== 'Skill Level')
      .map((searchResultTag) => {
        const id = searchResultTag.toLowerCase().replace(' ', '_');

        return {
          id,
          headerClassName: 'w-2/12 bg-gray-200',
          className: 'w-2/12',
          label: (
            <ColumnSort
              className="cursor-pointer"
              selection={sortBy}
              onChange={onChangeSortBy}
              columnId={id}
              isDisabled={false}
              isReversed
            >
              {searchResultTag}
            </ColumnSort>
          ),
          body: (row) => {
            const { displayTags } = row;
            let tags = getTags(displayTags, searchResultTag);

            if (tags.length === 0) {
              return '-';
            }

            const isSkillChip = searchResultTag === 'Skill';

            if (isSkillChip) {
              if (tags.length === 1 && tags[0].label.toLowerCase() === 'all') {
                return (
                  <TagWithDescriptionPopover
                    isSkillChip={isSkillChip}
                    tags={tags}
                  />
                );
              }

              let skillLevelTags = getTags(displayTags, 'Skill Level');

              tags = tags.map((tag) => {
                const [, skillLabel] = tag.label.split('Skill ');
                const [sortKey1, sortKey2] = skillLabel.split('.');
                const skillLevelTag = skillLevelTags.find(
                  (t) => t.label === `Skill Level ${sortKey1}`,
                );

                return {
                  label: skillLabel,
                  category: skillLevelTag ? skillLevelTag.description : null,
                  name: tag.description,
                  sortKey1: Number.parseInt(sortKey1, 10),
                  sortKey2,
                };
              });

              return (
                <PracticeSkillsPopOver
                  widgetTagColumn={widgetTagColumn}
                  widgetTagColumnLabel={widgetTagColumnLabel}
                  skills={tags}
                />
              );
            }

            return <TagWithDescriptionPopover tags={tags} />;
          },
        };
      }),
    {
      id: 'actions',
      label: '',
      headerClassName: 'w-1/12 bg-gray-200 rounded-r-0.4',
      useFlexDisplay: true,
      className: 'w-1/12',
      style: { padding: '0' },
      body: (row) => {
        const { resource, unit: unitNumber } = row;
        const { __typename: type } = resource;
        let ppcCourseOutlineData;
        ppcCourseOutlineData =
          (type !== ResourceTypes.SPP &&
            ppcsCourseOutlineData.find(
              (data) => data.resource.resourceId === resource.id,
            )) ||
          {};

        return (
          <ActionContainer
            type={type}
            unitNumber={unitNumber}
            resource={resource}
            units={units}
            isPreApSubject={isPreApSubject}
            widgetTagColumn={widgetTagColumn}
            unitsLabel={unitsLabel}
            teacherAssignments={get(teacherAssignments, 'assignments', [])}
            ppcCourseOutlineData={ppcCourseOutlineData}
            subunitLabel={subunitLabel}
          />
        );
      },
    },
  ];

  return (
    <Fragment>
      <Table
        className="ResourceResults--table"
        cols={columns}
        rows={resources
          .filter(removePEAssessment)
          .map((r, i) => (i === 0 ? { ...r, className: 'without-border' } : r))}
        keyFunc={(r) => r.resource.id}
        withoutRowsTopBorder
      />
      {isLoading ? (
        <div className="mt-4.6">
          <LoadingSpinner />
        </div>
      ) : (
        resources.length === 0 && (
          <div className="mt-4.6 text-center text-12 text-gray-600">
            No resources found.
          </div>
        )
      )}
    </Fragment>
  );
};

ResultsTable.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  resources: PropTypes.array.isRequired,
  pushParamsToHistory: PropTypes.func.isRequired,
  units: PropTypes.array,
  isPreApSubject: PropTypes.bool,
  subjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  widgetTagColumn: PropTypes.string,
  widgetTagColumnLabel: PropTypes.string,
  unitsLabel: PropTypes.string,
  router: PropTypes.object.isRequired,
  searchTerm: PropTypes.string,
  searchType: PropTypes.string,
  sortBy: PropTypes.string,
  teacherAssignments: PropTypes.object,
  loadingAssignments: PropTypes.bool,
  searchResultsTags: PropTypes.string,
  subunitLabel: PropTypes.string,
};

ResultsTable.defaultProps = {
  units: [],
  isPreApSubject: false,
  subjectId: null,
  widgetTagColumn: null,
  widgetTagColumnLabel: null,
  unitsLabel: null,
  searchTerm: '',
  searchType: '',
  sortBy: '',
  teacherAssignments: {},
  loadingAssignments: false,
  searchResultsTags: '',
  subunitLabel: null,
};

export default ResultsTable;
